import { useQuery } from '@tanstack/react-query';
import { useApiMutation } from '../../api/mutations';
import { nexusFetch } from '../../nexus/nexusClient';
import { useFixturesMode, fixtureDelay } from '../../api/fixtures';
import { availableDeviceFixture, customerDevicesFixture } from './fixture';

/**
 * Dedicated-Server-Zuordnung (N3 NetBox-Routing). M2M-Auth wie alle
 * anderen /v1/admin/*-Endpoints — kein Bearer nötig.
 *
 * Endpoints: GET /v1/admin/dedicated/available, POST .../reserve,
 * POST .../release, GET .../customer/:id
 */

/** GET /v1/admin/dedicated/available?role=&site= */
export function useAvailableDedicated({ role, site = '' }) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-dedicated-available', { role, site }, fixturesMode ? 'fixture' : 'live'],
    enabled: !!role,
    queryFn: async () => {
      if (fixturesMode) { await fixtureDelay(); return availableDeviceFixture({ role }); }
      const qs = new URLSearchParams({ role });
      if (site) qs.set('site', site);
      return nexusFetch(`/v1/admin/dedicated/available?${qs.toString()}`);
    },
  });
}

/** GET /v1/admin/dedicated/customer/:id */
export function useCustomerDedicatedDevices(customerId) {
  const fixturesMode = useFixturesMode();
  return useQuery({
    queryKey: ['nexus-dedicated-customer', String(customerId ?? ''), fixturesMode ? 'fixture' : 'live'],
    enabled: !!customerId,
    queryFn: async () => {
      if (fixturesMode) { await fixtureDelay(); return customerDevicesFixture(customerId); }
      return nexusFetch(`/v1/admin/dedicated/customer/${encodeURIComponent(customerId)}`);
    },
  });
}

/** POST /v1/admin/dedicated/reserve — { device_id, customer_id } */
export function useReserveDedicated() {
  return useApiMutation({
    live: (_fetch, { deviceId, customerId }) =>
      nexusFetch('/v1/admin/dedicated/reserve', {
        method: 'POST',
        body: { device_id: deviceId, customer_id: customerId },
      }),
    fixture: ({ deviceId, customerId }) => ({ device: { id: deviceId, dedicated_to_customer_id: customerId } }),
    invalidate: ['nexus-dedicated-available', 'nexus-dedicated-customer'],
    successMessage: (res) => `Device ${res?.device?.name ?? res?.device?.id} reserviert.`,
  });
}

/** POST /v1/admin/dedicated/release — { device_id } */
export function useReleaseDedicated() {
  return useApiMutation({
    live: (_fetch, { deviceId }) =>
      nexusFetch('/v1/admin/dedicated/release', {
        method: 'POST',
        body: { device_id: deviceId },
      }),
    fixture: ({ deviceId }) => ({ device: { id: deviceId, dedicated_to_customer_id: null } }),
    invalidate: ['nexus-dedicated-available', 'nexus-dedicated-customer'],
    successMessage: (res) => `Device ${res?.device?.name ?? res?.device?.id} freigegeben.`,
  });
}
